import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

interface EndUserMessageInputProps {
  onSendMessage: (content: string, attachments?: File[]) => void;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

const EndUserMessageInput: React.FC<EndUserMessageInputProps> = ({
  onSendMessage,
  onTypingStart,
  onTypingStop,
  disabled = false,
  placeholder = 'メッセージを入力... (Shift+Enterで改行)',
  maxLength = 10000,
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dragCounterRef = useRef(0);

  // ファイル制限
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];

  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.csv', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx'];

  // タイピング状態管理
  useEffect(() => {
    if (message.trim() && !isTyping) {
      setIsTyping(true);
      onTypingStart?.();
    } else if (!message.trim() && isTyping) {
      setIsTyping(false);
      onTypingStop?.();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (message.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTypingStop?.();
      }, 3000);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, isTyping, onTypingStart, onTypingStop]);

  useEffect(() => {
    return () => {
      if (isTyping) {
        onTypingStop?.();
      }
    };
  }, [isTyping, onTypingStop]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= maxLength) {
      setMessage(value);
    }
  };

  // Enterキーの動作変更: Enterは改行のみ（送信は送信ボタンのみ）
  const handleKeyPress = (event: React.KeyboardEvent) => {
    // Enterキーは改行のみ（送信は送信ボタンのみ）
    // 何もしない - デフォルトの改行動作を許可
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && !attachedFile) || disabled || isSending) {
      return;
    }

    await executeSend(trimmedMessage);
  };

  const executeSend = async (messageToSend: string) => {
    setIsSending(true);
    try {
      await onSendMessage(messageToSend, attachedFile ? [attachedFile] : undefined);
      setMessage('');
      setAttachedFile(null);
      setFileError(null);
      setIsTyping(false);
      onTypingStop?.();

      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      setMessage('');
      setAttachedFile(null);
      setIsTyping(false);
      onTypingStop?.();
    } finally {
      setIsSending(false);
    }
  };

  // ファイル添付
  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  // 画像リサイズ関数
  const resizeImage = async (file: File, maxWidth: number, quality: number = 0.85): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // アスペクト比を維持してリサイズ
        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxWidth) {
          width = Math.round((width * maxWidth) / height);
          height = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Blob conversion failed'));
              return;
            }
            const resizedFile = new File([blob], file.name, { type: file.type });
            resolve(resizedFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Image load failed'));
      };
      
      img.src = url;
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルタイプチェック
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setFileError('画像、PDF、CSV、Excel、Word、PowerPointファイルのみアップロードできます');
      event.target.value = '';
      return;
    }

    const isImage = /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);
    if (isImage) {
      try {
        const resizedFile = await resizeImage(file, 1600, 0.85);
        if (resizedFile.size > MAX_FILE_SIZE) {
          setFileError(`リサイズ後もファイルサイズが大きすぎます (${(resizedFile.size / 1024 / 1024).toFixed(2)}MB)。`);
          event.target.value = '';
          return;
        }
        console.log(`✅ 画像を自動リサイズ: ${(file.size / 1024).toFixed(1)}KB → ${(resizedFile.size / 1024).toFixed(1)}KB`);
        setAttachedFile(resizedFile);
        setFileError(null);
      } catch (error) {
        console.error('❌ 画像リサイズエラー:', error);
        setFileError('画像のリサイズに失敗しました。もう一度お試しください。');
      }
    } else {
      if (file.size > MAX_FILE_SIZE) {
        setFileError('ファイルサイズは5MB以下にしてください');
        event.target.value = '';
        return;
      }
      setAttachedFile(file);
      setFileError(null);
    }
    
    event.target.value = '';
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    setFileError(null);
  };

  // ファイルバリデーション共通関数（画像は自動リサイズ）
  const validateAndAttachFile = async (file: File) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setFileError('画像、PDF、CSV、Excel、Word、PowerPointファイルのみアップロードできます');
      return;
    }

    const isImage = /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);
    if (isImage) {
      try {
        const resizedFile = await resizeImage(file, 1600, 0.85);
        if (resizedFile.size > MAX_FILE_SIZE) {
          setFileError(`リサイズ後もファイルサイズが大きすぎます (${(resizedFile.size / 1024 / 1024).toFixed(2)}MB)。`);
          return;
        }
        console.log(`✅ 画像を自動リサイズ: ${(file.size / 1024).toFixed(1)}KB → ${(resizedFile.size / 1024).toFixed(1)}KB`);
        setAttachedFile(resizedFile);
        setFileError(null);
      } catch (error) {
        console.error('❌ 画像リサイズエラー:', error);
        setFileError('画像のリサイズに失敗しました。もう一度お試しください。');
      }
    } else {
      if (file.size > MAX_FILE_SIZE) {
        setFileError('ファイルサイズは5MB以下にしてください');
        return;
      }
      setAttachedFile(file);
      setFileError(null);
    }
  };

  // ドラッグ&ドロップハンドラ
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndAttachFile(files[0]);
    }
  };

  const remainingChars = maxLength - message.length;
  const isNearLimit = remainingChars <= 500;

  return (
    <>
      <Paper
        elevation={2}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          p: 1,
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000,
          transition: 'border-color 0.2s ease',
          ...(isDragOver && {
            borderColor: 'primary.main',
            borderStyle: 'dashed',
            borderWidth: 2,
          }),
        }}
      >
        {/* ドラッグ&ドロップ オーバーレイ */}
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              borderRadius: 'inherit',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                ここにファイルをドロップ
              </Typography>
            </Box>
          </Box>
        )}

        {/* ファイルエラー表示 */}
        {fileError && (
          <Alert severity="error" onClose={() => setFileError(null)} sx={{ mb: 1 }}>
            {fileError}
          </Alert>
        )}

      {/* 添付ファイル表示 */}
      {attachedFile && (
        <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">{attachedFile.name} ({(attachedFile.size / 1024).toFixed(1)} KB)</Typography>
          </Box>
          <IconButton size="small" onClick={handleRemoveFile}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* ファイル添付ボタン */}
        <Tooltip title="ファイル添付 (画像・PDF・Office形式, 5MB以下)">
          <IconButton
            onClick={handleAttachFile}
            disabled={disabled}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* メッセージ入力フィールド */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <TextField
            ref={textFieldRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || isSending}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 20,
                backgroundColor: '#f8f9fa',
                paddingRight: '8px',
                '&:hover': {
                  backgroundColor: '#f1f3f4',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '0.95rem',
              },
            }}
          />

          {isNearLimit && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 8,
                color: remainingChars <= 100 ? 'error.main' : 'warning.main',
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255,255,255,0.9)',
                px: 0.5,
                borderRadius: 0.5,
              }}
            >
              残り {remainingChars.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* 送信ボタン */}
        <Tooltip title="送信 (送信ボタンクリック)">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={(!message.trim() && !attachedFile) || disabled || isSending}
              color="primary"
              size="small"
              sx={{
                width: 36,
                height: 36,
                backgroundColor: (message.trim() || attachedFile) && !disabled && !isSending ? 'primary.main' : '#e0e0e0',
                color: 'white',
                '&:hover': {
                  backgroundColor: (message.trim() || attachedFile) && !disabled && !isSending ? 'primary.dark' : '#e0e0e0',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#bdbdbd',
                },
              }}
            >
              {isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
    </>
  );
};

export default EndUserMessageInput;

